/* Speech utilities using the browser Web Speech API.
 *
 * Android Chrome cuts off long utterances (either immediately or after ~15s),
 * and drops utterances whose language voice isn't installed. We work around
 * both by:
 *   1) Sanitising markdown / LaTeX to plain text
 *   2) Splitting text into short sentence-sized chunks (~180 chars)
 *   3) Speaking chunks sequentially via onend chaining
 *   4) Picking a working English voice (en-IN → en-US → en-GB → first English)
 *   5) Keeping state via onstart / onend / onerror callbacks (no polling)
 */

const state = {
  chunks: [],      // remaining chunks (strings)
  current: null,   // current SpeechSynthesisUtterance
  onEnd: null,     // caller callback when all chunks are done
  onError: null,   // caller callback for error
  cancelled: false,
};

function cleanForSpeech(text) {
  return String(text || "")
    // remove $$...$$ / $...$ math blocks
    .replace(/\$\$[^$]*\$\$/g, " ")
    .replace(/\$[^$\n]*\$/g, " ")
    // remove markdown links [txt](url) → txt
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    // remove images ![alt](url)
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    // strip markdown chars
    .replace(/[#*_`>~]/g, "")
    // collapse whitespace
    .replace(/\s+/g, " ")
    .trim();
}

/** Split into <=180 char pieces, preferring sentence / clause boundaries. */
function chunkText(text, max = 180) {
  const clean = cleanForSpeech(text);
  if (!clean) return [];
  const sentences = clean.match(/[^.!?]+[.!?]?/g) || [clean];
  const out = [];
  let buf = "";
  for (const s of sentences) {
    const seg = s.trim();
    if (!seg) continue;
    if (seg.length > max) {
      // hard split long segments by commas / spaces
      const parts = seg.split(/(?<=[,;:])\s+/);
      for (const p of parts) {
        if (p.length > max) {
          // last resort: split by word boundary
          let i = 0;
          while (i < p.length) {
            out.push(p.slice(i, i + max));
            i += max;
          }
        } else {
          out.push(p);
        }
      }
      continue;
    }
    if ((buf + " " + seg).trim().length <= max) {
      buf = (buf + " " + seg).trim();
    } else {
      if (buf) out.push(buf);
      buf = seg;
    }
  }
  if (buf) out.push(buf);
  return out;
}

let cachedVoice = null;
function pickVoice() {
  if (cachedVoice) return cachedVoice;
  const voices = window.speechSynthesis?.getVoices?.() || [];
  if (!voices.length) return null;
  cachedVoice =
    voices.find((v) => /^en[-_]IN/i.test(v.lang)) ||
    voices.find((v) => /^en[-_]US/i.test(v.lang)) ||
    voices.find((v) => /^en[-_]GB/i.test(v.lang)) ||
    voices.find((v) => /^en/i.test(v.lang)) ||
    voices[0];
  return cachedVoice;
}

// Voices load asynchronously on some browsers — warm the cache when available.
if (typeof window !== "undefined" && "speechSynthesis" in window) {
  try {
    window.speechSynthesis.onvoiceschanged = () => { cachedVoice = null; pickVoice(); };
    pickVoice();
  } catch (_) { /* noop */ }
}

function speakNextChunk() {
  if (state.cancelled) return;
  const next = state.chunks.shift();
  if (!next) {
    state.current = null;
    const cb = state.onEnd;
    state.onEnd = null; state.onError = null;
    cb && cb();
    return;
  }
  const u = new SpeechSynthesisUtterance(next);
  u.rate = 1.0;
  u.pitch = 1.0;
  const v = pickVoice();
  if (v) { u.voice = v; u.lang = v.lang; } else { u.lang = "en-US"; }
  u.onend = () => { if (!state.cancelled) speakNextChunk(); };
  u.onerror = (ev) => {
    // "interrupted"/"canceled" happen on cancel() — silent
    if (ev?.error && ev.error !== "interrupted" && ev.error !== "canceled") {
      const cb = state.onError;
      state.onError = null; state.onEnd = null;
      state.chunks = [];
      state.current = null;
      cb && cb(ev.error);
      return;
    }
    // otherwise, try next
    if (!state.cancelled) speakNextChunk();
  };
  state.current = u;
  try { window.speechSynthesis.speak(u); } catch (_) { /* noop */ }
}

/**
 * Speak the given text. Options:
 *   onStart():   called once when first chunk starts
 *   onEnd():     called when all chunks finish (or cancelled)
 *   onError(e):  called on unrecoverable error
 * Returns true if speech was queued, false if unsupported.
 */
export function speak(text, { onStart, onEnd, onError } = {}) {
  if (!("speechSynthesis" in window)) return false;
  const chunks = chunkText(text);
  if (!chunks.length) return false;

  // cancel anything currently speaking
  state.cancelled = true;
  try { window.speechSynthesis.cancel(); } catch (_) { /* noop */ }

  // reset for new run
  state.cancelled = false;
  state.chunks = chunks;
  state.current = null;
  state.onEnd = onEnd || null;
  state.onError = onError || null;

  // fire onStart on the first utterance
  const originalNext = speakNextChunk;
  const startWrapper = () => {
    const first = state.chunks[0];
    if (!first) return originalNext();
    // temporarily wrap: attach onstart to the first utterance
    const u = new SpeechSynthesisUtterance(first);
    u.rate = 1.0; u.pitch = 1.0;
    const v = pickVoice();
    if (v) { u.voice = v; u.lang = v.lang; } else { u.lang = "en-US"; }
    u.onstart = () => { onStart && onStart(); };
    u.onend = () => { if (!state.cancelled) originalNext(); };
    u.onerror = (ev) => {
      if (ev?.error && ev.error !== "interrupted" && ev.error !== "canceled") {
        state.chunks = [];
        state.current = null;
        onError && onError(ev.error);
        return;
      }
      if (!state.cancelled) originalNext();
    };
    state.chunks.shift();
    state.current = u;
    try { window.speechSynthesis.speak(u); } catch (_) { /* noop */ }
  };
  startWrapper();
  return true;
}

export function stopSpeaking() {
  state.cancelled = true;
  state.chunks = [];
  state.current = null;
  state.onEnd = null;
  state.onError = null;
  if ("speechSynthesis" in window) {
    try { window.speechSynthesis.cancel(); } catch (_) { /* noop */ }
  }
}

export function isSpeaking() {
  return !!state.current && !state.cancelled;
}

export function createRecognizer(onResult, onEnd) {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) return null;
  const rec = new SR();
  rec.lang = "en-IN";
  rec.continuous = false;
  rec.interimResults = false;
  rec.onresult = (e) => {
    const text = Array.from(e.results).map((r) => r[0].transcript).join(" ");
    onResult && onResult(text);
  };
  rec.onend = () => onEnd && onEnd();
  return rec;
}

export const SCI_SYMBOLS = [
  "±", "×", "÷", "≈", "≠", "≤", "≥", "→", "⇌",
  "α", "β", "γ", "δ", "θ", "λ", "μ", "π", "Σ", "Ω",
  "°", "²", "³", "√", "∫", "∞", "Δ", "∝", "∴",
  "H₂O", "CO₂", "NH₃", "H⁺", "OH⁻",
];
